// Számla-szerkesztő (full-page, edit-mód) — CSAK draft (nem-draft → zárolt üzenet). A form a @atlas/contracts
// draft-body mezőit szerkeszti (fej + TELJES tétel-lista; a BFF diff-el). Mentés: PUT If-Match/OCC (a fej
// verziója a diff-horgony); 422 → mező, 409 → „valaki módosította" toast + refetch, 403 → toast, 428 →
// hiányzó verzió. A kliens ÖSSZEGET NEM küld — a totálokat a szerver számolja. Siker → vissza a listára.
// Page-gate: invoices.manage.
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { FormPage } from '@/components/shared/form-page';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { invoiceKeys } from '@/features/invoices/api';
import { InvoiceForm } from '@/features/invoices/components/invoice-form';
import {
  buildInvoiceErrorMap,
  emptyInvoiceForm,
  invoiceFormSchema,
  invoiceFormToDraftBody,
  invoiceToFormValues,
  todayIsoDate,
  type InvoiceFormValues,
} from '@/features/invoices/form';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

export const Route = createFileRoute('/invoices/$id/edit')({
  beforeLoad: () => requireAuth(),
  component: InvoiceEditPage,
});

type DetailPage = Awaited<ReturnType<typeof api.invoiceDetail.query>>;

function InvoiceEditPage(): JSX.Element {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canManage = can('invoices.manage');
  const gated = !permsLoading && !canManage;

  const detailKey = invoiceKeys.detail(id);
  const detailQuery = api.invoiceDetail.useQuery(
    detailKey,
    { params: { id } },
    { queryKey: detailKey },
  );
  const detail = detailQuery.data?.status === 200 ? detailQuery.data.body : undefined;
  const version = detail?.version;
  const isDraft = detail?.status === 'draft';

  const clientId = detail?.clientId ?? '';
  const refsKey = invoiceKeys.refs(clientId || '__none__');
  const refsQ = api.invoiceRefs.useQuery(
    refsKey,
    { query: clientId ? { clientId } : {} },
    { queryKey: refsKey, enabled: clientId !== '' },
  );
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  const today = todayIsoDate();
  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema, {
      errorMap: buildInvoiceErrorMap(t),
    }) as Resolver<InvoiceFormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: emptyInvoiceForm(today, today),
  });

  const { reset } = form;
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    // CSAK amíg a felhasználó nem piszkálta a formot — egy háttér-refetch ne írja felül a szerkesztést.
    if (detail && !isDirty) reset(invoiceToFormValues(detail));
  }, [detail, isDirty, reset]);

  const updateMutation = useEntityMutation<InvoiceFormValues, DetailPage>({
    mutationFn: (values) =>
      api.invoiceUpdate.mutation({
        params: { id },
        body: invoiceFormToDraftBody(values),
        headers: { 'if-match': version !== undefined ? String(version) : undefined },
      }),
    invalidateKeys: [detailKey, invoiceKeys.lists()],
    onFieldErrors: (field, messages) =>
      form.setError(field as keyof InvoiceFormValues, { message: messages.join(', ') }),
    onConflict: () => void detailQuery.refetch(),
    messages: {
      success: t('toast:invoices.saved'),
      conflict: t('toast:invoices.conflict'),
      forbidden: t('toast:invoices.forbidden'),
      validation: t('toast:invoices.validation'),
      error: t('toast:invoices.error'),
    },
  });

  function onSubmit(values: InvoiceFormValues): void {
    updateMutation.mutate(values, {
      onSuccess: () => void navigate({ to: '/invoices' }),
    });
  }

  const backButton = (
    <Button variant="outline" onClick={() => void navigate({ to: '/invoices' })}>
      <ArrowLeftIcon className="h-4 w-4" />
      {t('invoices.form.back')}
    </Button>
  );

  if (gated) {
    return (
      <AppShell>
        <FormPage>
          <EmptyState
            title={t('invoices.forbidden')}
            description={t('invoices.forbiddenDesc')}
            action={backButton}
          />
        </FormPage>
      </AppShell>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <AppShell>
        <FormPage>
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-64 w-full" />
          </div>
        </FormPage>
      </AppShell>
    );
  }

  if (!detail) {
    return (
      <AppShell>
        <FormPage>
          <EmptyState
            title={t('invoices.notFound')}
            description={t('invoices.notFoundDesc')}
            action={backButton}
          />
        </FormPage>
      </AppShell>
    );
  }

  // Nem-draft → nem szerkeszthető (a Számlázz.hu-kiállítás a Függő increment). Read-only nézet még nincs.
  if (!isDraft) {
    return (
      <AppShell>
        <FormPage>
          <EmptyState
            title={t('invoices.notEditableTitle')}
            description={t('invoices.notEditableDesc')}
            action={backButton}
          />
        </FormPage>
      </AppShell>
    );
  }

  // A cím a TOPBAR morzsájában él („Számlák / <számlaszám>") — az oldal törzsében nincs h1, mert duplikálná.
  // A bizonylatszám a piszkozat ideiglenes száma (`TMP-INV-…`); a régi, technikai `DRAFT-{uuid}` sorokra a
  // „Piszkozat" placeholder jut — a részletes nézet és a lista UGYANEZT a szabályt használja.
  const num = detail.invoiceNumber;
  const displayNumber =
    num === null || num === '' || num.startsWith('DRAFT-')
      ? t('invoices.draftNumberPlaceholder')
      : num;
  return (
    <AppShell
      parent={{ label: t('nav.invoices'), to: '/invoices' }}
      title={displayNumber}
      subtitle={t('invoices.editSubtitle')}
    >
      <FormPage>
        <InvoiceForm
          form={form}
          refs={refs}
          mode="edit"
          pending={updateMutation.isPending}
          onSubmit={onSubmit}
          t={t}
        />
      </FormPage>
    </AppShell>
  );
}
